import React, { useEffect, useState } from "react";
import { View, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Empty from "components/Empty";
import Error from "components/Error";
import Loading from "components/Loading";
import AnimatedInfinityList from "components/AnimatedInfinityList";

import { styles } from "./styles";
import { sizes, colors, strings } from "assets/constants";

import { useFlag } from "hooks";
import { mq } from "utils";

import { useCategories } from "clients/apis";
import ModalInfinityList from "components/ModalInfinityList";

const unregisteredItem = {
  id: null,
  title: strings.unregistered,
  name: strings.unregistered,
  text: strings.unregistered,
};

export default function ParentCatPicker(
  props: PickerT & {
    showChild?: boolean;
    hideParents?: boolean;
    showUnregisteredItem?: boolean;
  }
) {
  const {
    id,
    value,
    onSelect,
    error,
    hideError,
    onPress,
    placeholder,
    disabled,
  } = props;
  const val = value as CategoryT;

  const {
    isError,
    error: catError,
    isRefetching,
    refetch,
    isFetching: categoriesFetching,
    data: categories,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCategories();

  const { flg: modal, on: show, off: hide } = useFlag();

  const _onHide = hide;

  const [valueState, setValueState] = useState<CategoryT | undefined>();

  useEffect(() => {
    setValueState(val);
  }, [val]);

  useEffect(() => {
    id && refetch();
  }, [id, refetch]);

  const _renderItem = (items: Array<any>) => {
    return items.map((cat: CategoryT, key: number) => (
      <React.Fragment key={String(key)}>
        <>
          {props.hideParents && cat?.children?.length > 0 ? null : (
            <>
              <TouchableOpacity
                activeOpacity={sizes.tapOpacity}
                style={styles.item}
                onPress={() => {
                  onSelect && onSelect(cat);
                  setValueState(cat);
                  hide();
                }}
              >
                <Text>{cat.title}</Text>
              </TouchableOpacity>
              <View style={styles.itemSeparator} />
            </>
          )}
        </>
        {props.showChild &&
          Number(cat.children?.length) > 0 &&
          cat.children?.map((child: CategoryT, childKey: number) => (
            <React.Fragment key={String(childKey)}>
              <TouchableOpacity
                activeOpacity={sizes.tapOpacity}
                style={styles.item}
                onPress={() => {
                  onSelect && onSelect(child);
                  setValueState(child);
                  hide();
                }}
              >
                <Text>{child.title}</Text>
              </TouchableOpacity>
              <View style={styles.itemSeparator} />
            </React.Fragment>
          ))}
      </React.Fragment>
    ));
  };

  const _renderList = () => {
    if (categoriesFetching) {
      return <Loading style={{ marginTop: mq(20) }} />;
    }

    if (isError) {
      return <Error error={catError} reset={refetch} loading={isRefetching} />;
    }

    if (categories?.pages?.[0]?.data?.data?.length === 0) {
      return <Empty style={{ minHeight: mq(100) }} message={strings.no_cat} />;
    }

    return (
      <>
        {props?.showUnregisteredItem && _renderItem([unregisteredItem])}
        <AnimatedInfinityList
          onRefresh={refetch}
          responseData={categories}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          renderItemList={_renderItem}
          // paddingBottom={50}
        />
      </>
    );
  };

  return (
    <>
      <>
        <TouchableOpacity
          disabled={disabled}
          onPress={onPress ?? show}
          activeOpacity={sizes.tapOpacity}
          style={{ ...styles.container }}
        >
          <View style={styles.iconContainer}>
            <Icon name="chevron-down" />
          </View>
          <View style={styles.textContainer}>
            <Text
              color={
                valueState?.title ? colors.textColorDefault : colors.placeholder
              }
            >
              {valueState?.title || placeholder || strings.pick_parent_cat}
            </Text>
          </View>

          <View style={styles.iconContainer}>
            <Icon name="grid" />
          </View>
        </TouchableOpacity>

        {!hideError && (
          <View style={styles.errorContainer}>
            <Text size={"xs"} color={colors.danger}>
              {error}
            </Text>
          </View>
        )}
      </>

      <ModalInfinityList
        contentContainerStyle={{
          paddingTop: 0,
          paddingBottom: 0,
          marginBottom: 0,
        }}
        visible={modal}
        close={_onHide}
      >
        {_renderList()}
      </ModalInfinityList>
    </>
  );
}
